import { useRef, useState } from 'react';
import styled from 'styled-components';

import ExternalAttachment from 'components/shared/ExternalAttachment';
import FileInputComponent, { FileInputHandle } from 'components/shared/fileInput/FileInputComponent';
import Upload from 'hooks/useDirectUpload/Upload';
import { WorkflowAttachmentOptions } from 'models/Workflow';
import { PacketEntry } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { packetAttachment } from './packetAttachment';

// Editing a referral's packet (OLIO-2773): the documents it already holds, each removable, with the
// dropzone still available to add more. Used by both surfaces that edit a packet — the referral
// modal and the full-page edit form — so the two can't drift into different behaviour.
//
// The state lives in the hook and the rows in the component, because the parent needs the packet
// (to send) and whether it changed (to enable Update, and to know the `attachments` value is part
// of what this save ripples into) while the rows are just how it's shown.

export function usePacketEditor(stored: WorkflowAttachmentOptions[]) {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<FileInputHandle>(null);
  // Which of the referral's documents this person has taken out. Held as the REMOVED ids rather
  // than a kept list: the workflow refetches after every save, and a kept snapshot taken at mount
  // would go stale against it. What remains derives from live data instead.
  const [removedIds, setRemovedIds] = useState<string[]>([]);

  const kept = stored.filter((attachment) => !removedIds.includes(attachment.id));
  // A file that failed to upload has no signed id, so it isn't in the packet that gets sent. Saving
  // over it would report success while dropping it — and would still apply any removals made in the
  // same session, so the person ends up with less than they had and no error to explain it. Raised
  // in review on OLIO-2773: `uploading` is already false by then (useDirectUpload clears it in its
  // `finally`), so nothing else was holding the save back.
  const failed = uploads.some((upload) => upload.hasError);
  // A referral that arrived with documents keeps at least one; the API refuses an empty packet on
  // one that had them. Gated on the RESULT, not on the act of removing, so taking the only document
  // out and putting another in is a replacement and still saves.
  const emptied = stored.length > 0 && kept.length === 0 && uploads.length === 0;
  // Removing a document is a change even though nothing was uploaded, so this can't be
  // `uploads.length > 0`.
  const changed = uploads.length > 0 || removedIds.length > 0;
  // What the packet should BE, not what's being added: kept documents by id, then the new uploads.
  // Undefined leaves the packet alone; an empty array is a person having removed all of it.
  const entries: PacketEntry[] | undefined = changed
    ? [...kept.map((attachment) => ({ id: attachment.id })), ...uploads]
    : undefined;

  return {
    uploads,
    failed,
    emptied,
    isUploading,
    kept,
    changed,
    entries,
    fileInputRef,
    setUploads,
    setIsUploading,
    // Drop everything this person did to the packet. The create modal's method tabs use it: a
    // packet has no place on the typing path. FileInputComponent's own state goes with it, because
    // PacketEditor is unmounted rather than hidden when the packet isn't what's being edited.
    reset: () => {
      setUploads([]);
      setRemovedIds([]);
    },
    removeAttachment: (id: string) => setRemovedIds((ids) => [...ids, id]),
    // The uploads live inside FileInputComponent, so removal goes back through it — otherwise the
    // row would reappear on its next onChange.
    removeUpload: (id: string) => fileInputRef.current?.removeUpload(id),
  };
}

export type PacketEditorState = ReturnType<typeof usePacketEditor>;

export default function PacketEditor({ packet }: { packet: PacketEditorState }) {
  const { kept, uploads } = packet;

  return (
    <>
      {/* The dropzone stays available while files are listed, and the packet is ONE list of rows —
          documents already attached and ones just added, each removable. Two lists would read as
          two packets, and the person is editing one. */}
      <DropZone data-testid='packet-dropzone'>
        <FileInputComponent
          ref={packet.fileInputRef}
          onChange={packet.setUploads}
          onUploading={packet.setIsUploading}
          uploadList='none'
        />
      </DropZone>
      {packet.emptied && <FailedNote data-testid='packet-emptied'>Add at least one document to save.</FailedNote>}
      {packet.failed && (
        <FailedNote data-testid='packet-upload-failed'>
          A file didn’t upload. Remove it and try again — saving now would leave it off the referral.
        </FailedNote>
      )}
      {(kept.length > 0 || uploads.length > 0) && (
        <Files data-testid='packet-files'>
          {kept.map((attachment) => (
            <ExternalAttachment
              key={attachment.id}
              attachment={packetAttachment(attachment)}
              attachableModel='Workflow'
              canModifyDocumentType={false}
              onDeleteAttachment={packet.removeAttachment}
              failureMessage={
                attachment.processingFailed ? (attachment.failure?.message ?? 'File couldn’t be processed.') : undefined
              }
            />
          ))}
          {uploads.map((upload) => (
            <ExternalAttachment
              key={upload.id}
              attachment={upload}
              error={upload.hasError}
              uploadProgress={upload.progress}
              canModifyDocumentType={false}
              onDeleteAttachment={packet.removeUpload}
            />
          ))}
        </Files>
      )}
    </>
  );
}

const DropZone = styled.div`
  margin-top: 8px;
`;

// The save is blocked while a file sits in the list having failed; without this the button is just
// mysteriously dead.
const FailedNote = styled(BodySmall)`
  margin-top: 8px;
  color: ${colors.red100};
`;

const Files = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;
